import { useState } from "react";
import type { ApiError } from "@/lib/api";
import type {
  RosterImportCommitResult,
  RosterImportCommitRow,
  RosterImportPreviewResult,
  RosterImportPreviewRow,
} from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { Button } from "@/components/ui/Button";
import { useRosterImport } from "../hooks/useRosterImport";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";

/**
 * CSV roster import modal — Coach > Roster (M6b).
 *
 * Ports the legacy `openCoachRosterImportModal` +
 * `_renderRosterImportResult` (`js/coaching/roster.js`).
 *
 * Two-step flow:
 *   1. Paste CSV → "Preview import" — dry-runs server-side,
 *      renders the per-row plan. Writes nothing.
 *   2. "Commit import" — writes the import (transactional). Only
 *      enabled once a CLEAN (zero-error) preview exists AND the
 *      CSV hasn't changed since that preview (the staleness
 *      guard: editing the textarea after a preview forces a
 *      re-preview, so a coach can't commit a plan they never saw).
 *
 * Both endpoints are team-admin-only server-side. The launch
 * button (`RosterPage`) is also role-gated, so reaching this
 * modal as a non-admin shouldn't happen — a 403 is surfaced
 * inline as a backstop.
 *
 * Privacy: guardian-invite `token_url` values are surfaced ONLY
 * when the commit response carries them (dev-token mode); never
 * logged, never templated unconditionally.
 */

const SAMPLE_CSV = `display_name,jersey_number,position,guardian_email,relationship,active
Avery Lopez,4,Defender,parent@example.com,guardian,true
Mika Chen,9,Forward,parent@example.com,guardian,true`;

export type RosterImportModalProps = {
  onClose: () => void;
};

export function RosterImportModal({ onClose }: RosterImportModalProps) {
  const toast = useToast();
  const importer = useRosterImport();
  const [csv, setCsv] = useState("");

  const trimmedEmpty = csv.trim().length === 0;
  // Commit is allowed only with a clean, fresh preview of the
  // CURRENT csv text.
  const canCommit =
    !trimmedEmpty &&
    importer.previewHasNoErrors &&
    !importer.previewIsStale(csv) &&
    !importer.isCommitting;

  const handleClose = () => {
    importer.reset();
    onClose();
  };

  const handlePreview = async () => {
    if (trimmedEmpty) return;
    try {
      await importer.preview(csv);
    } catch {
      // Error surfaces via `importer.previewError` below.
    }
  };

  const handleCommit = async () => {
    if (trimmedEmpty) return;
    // Staleness guard: if the CSV changed since the last preview,
    // re-preview first so the coach commits exactly what they saw.
    if (importer.previewIsStale(csv)) {
      try {
        const fresh = await importer.preview(csv);
        if (fresh.summary.errors > 0) return; // errors block commit
      } catch {
        return;
      }
    }
    try {
      const result = await importer.commit(csv);
      if (result.summary.errors === 0) {
        toast.success("Roster imported");
      } else {
        toast.error("Roster import finished with errors", {
          description: `${result.summary.errors} error row(s) — see the result list.`,
        });
      }
    } catch (err) {
      // Error also surfaces via `importer.commitError` below.
      toast.error("Couldn't import roster", { description: errorMessage(err) });
    }
  };

  const previewErrorText = errorText(importer.previewError);
  const commitErrorText = errorText(importer.commitError);

  return (
    <CoachModal
      title="Import players from CSV"
      onClose={handleClose}
      data-testid="roster-import-modal"
    >
      <div className="flex flex-col gap-4">
        {/* Column reference. */}
        <div className="rounded border border-rule bg-canvas p-3 text-xs text-ink-muted">
          <p className="font-display uppercase tracking-wider text-ink">
            CSV columns
          </p>
          <p className="mt-1">
            Required: <code>display_name</code>. Optional:{" "}
            <code>player_id</code>, <code>jersey_number</code>,{" "}
            <code>position</code>, <code>guardian_email</code>,{" "}
            <code>relationship</code>, <code>active</code>.
          </p>
        </div>

        {/* CSV input. */}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Paste CSV
          </span>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            spellCheck={false}
            rows={8}
            data-testid="roster-import-csv"
            placeholder="display_name,jersey_number,position,guardian_email,relationship,active"
            className="w-full rounded border border-rule bg-canvas px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCsv(SAMPLE_CSV)}
            data-testid="roster-import-sample"
            className="rounded border border-rule px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink-muted transition-colors hover:bg-field hover:text-ink"
          >
            Use sample
          </button>
          <Button
            type="button"
            onClick={handlePreview}
            disabled={trimmedEmpty || importer.isPreviewing}
            data-testid="roster-import-preview"
            variant="outline"
          >
            {importer.isPreviewing ? "Previewing…" : "Preview import"}
          </Button>
          <Button
            type="button"
            onClick={handleCommit}
            disabled={!canCommit}
            data-testid="roster-import-commit"
          >
            {importer.isCommitting ? "Importing…" : "Commit import"}
          </Button>
        </div>

        {/* Staleness hint — the preview no longer matches the
         * textarea, so commit is gated until a re-preview. */}
        {importer.previewResult &&
        !importer.commitResult &&
        importer.previewIsStale(csv) &&
        !trimmedEmpty ? (
          <p
            className="text-ink-muted font-mono text-xs"
            data-testid="roster-import-stale-hint"
          >
            CSV changed since the last preview — preview again before
            importing.
          </p>
        ) : null}

        {/* Errors. */}
        {previewErrorText ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-import-preview-error"
          >
            {previewErrorText}
          </p>
        ) : null}
        {commitErrorText ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-import-commit-error"
          >
            {commitErrorText}
          </p>
        ) : null}

        {/* Result — commit result takes precedence over preview. */}
        {importer.commitResult ? (
          <CommitResultView
            result={importer.commitResult}
            onDone={handleClose}
          />
        ) : importer.previewResult ? (
          <PreviewResultView result={importer.previewResult} />
        ) : null}
      </div>
    </CoachModal>
  );
}

/** The backend's actionable error message, or a generic fallback.
 * `apiFetch` already unwrapped FastAPI's `{ detail: ... }`
 * envelope, so `err.detail` IS the message string for the
 * 403 / 409 (wrong-team) / 422 import-error paths. */
function errorText(err: ApiError | null): string | null {
  if (!err) return null;
  if (typeof err.detail === "string") return err.detail;
  return "Something went wrong with the import.";
}

const ROW_LIMIT = 12;

function PreviewResultView({
  result,
}: {
  result: RosterImportPreviewResult;
}) {
  const s = result.summary;
  return (
    <div className="flex flex-col gap-2" data-testid="roster-import-preview-result">
      <SummaryRow
        items={[
          ["Rows", s.rows],
          ["Errors", s.errors],
          ["To create", s.create_players],
          ["To update", s.update_players],
          ["Guardian invites", s.guardian_invites],
          ["Existing guardians", s.linked_existing_users],
        ]}
      />
      <RowList
        rows={result.rows}
        render={(row) => (
          <PreviewRowItem key={row.row_number} row={row} />
        )}
      />
    </div>
  );
}

function CommitResultView({
  result,
  onDone,
}: {
  result: RosterImportCommitResult;
  onDone: () => void;
}) {
  const s = result.summary;
  const devInvites = result.guardian_invites.filter((inv) => inv.token_url);
  return (
    <div className="flex flex-col gap-2" data-testid="roster-import-commit-result">
      <p
        className="text-ink font-display text-sm"
        data-testid="roster-import-commit-success"
      >
        {s.errors === 0
          ? `Import complete — ${s.created_players} created, ${s.existing_players} existing.`
          : `Import finished with ${s.errors} error row(s).`}
      </p>
      <SummaryRow
        items={[
          ["Rows", s.rows],
          ["Errors", s.errors],
          ["Created", s.created_players],
          ["Existing", s.existing_players],
          ["Guardian invites", s.guardian_invites],
          ["Guardians linked", s.linked_existing_users],
        ]}
      />
      <RowList
        rows={result.rows}
        render={(row) => <CommitRowItem key={row.row_number} row={row} />}
      />

      {/* Dev-mode invite links — shown ONLY when the server sent
       * `token_url` (TKTIC_DEV_TOKEN_DELIVERY). Never in prod. */}
      {devInvites.length > 0 ? (
        <div
          className="rounded border border-rule bg-canvas p-2 text-xs"
          data-testid="roster-import-dev-invites"
        >
          <p className="font-display uppercase tracking-wider text-ink-muted">
            Dev invite links
          </p>
          {devInvites.map((inv) => (
            <p key={inv.id} className="font-mono mt-1 break-all">
              {inv.normalized_email}:{" "}
              <a
                href={inv.token_url}
                className="text-accent underline"
                target="_blank"
                rel="noreferrer"
              >
                {inv.token_url}
              </a>
            </p>
          ))}
        </div>
      ) : null}

      <Button
        type="button"
        onClick={onDone}
        data-testid="roster-import-done"
        className="self-start"
      >
        Done
      </Button>
    </div>
  );
}

function SummaryRow({ items }: { items: Array<[string, number]> }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {items.map(([label, value]) => (
        <span key={label} className="text-ink-muted">
          {label}:{" "}
          <strong className="text-ink tabular-nums">{value}</strong>
        </span>
      ))}
    </div>
  );
}

function RowList<T extends { row_number: number }>({
  rows,
  render,
}: {
  rows: T[];
  render: (row: T) => React.ReactNode;
}) {
  const shown = rows.slice(0, ROW_LIMIT);
  const overflow = rows.length - shown.length;
  return (
    <ul className="flex flex-col gap-1" data-testid="roster-import-rows">
      {shown.map(render)}
      {overflow > 0 ? (
        <li className="text-ink-muted text-xs font-mono px-2 py-1">
          +{overflow} more row{overflow === 1 ? "" : "s"} not shown
        </li>
      ) : null}
    </ul>
  );
}

function PreviewRowItem({ row }: { row: RosterImportPreviewRow }) {
  const isError = row.status === "error";
  return (
    <li
      data-testid="roster-import-row"
      data-status={row.status}
      className={[
        "rounded border px-2 py-1 text-xs",
        isError
          ? "border-destructive/40 bg-destructive/5"
          : "border-rule bg-canvas",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-ink-muted">#{row.row_number}</span>
        <span className="text-ink font-display">
          {row.player?.display_name || "(missing name)"}
        </span>
        {row.player?.jersey_number ? (
          <span className="text-ink-muted">#{row.player.jersey_number}</span>
        ) : null}
        {row.player?.position ? (
          <span className="text-ink-muted">· {row.player.position}</span>
        ) : null}
        {row.guardian_email ? (
          <span className="text-ink-muted">· {row.guardian_email}</span>
        ) : null}
      </div>
      {!isError ? (
        <div className="text-ink-muted mt-0.5">
          {row.player_action} · {row.guardian_action}
        </div>
      ) : null}
      {row.warnings && row.warnings.length > 0 ? (
        <div className="text-ink-muted mt-0.5">
          {row.warnings.join("; ")}
        </div>
      ) : null}
      {isError && row.errors && row.errors.length > 0 ? (
        <div className="text-destructive mt-0.5">{row.errors.join("; ")}</div>
      ) : null}
    </li>
  );
}

function CommitRowItem({ row }: { row: RosterImportCommitRow }) {
  const isError = row.status === "error";
  return (
    <li
      data-testid="roster-import-row"
      data-status={row.status}
      className={[
        "rounded border px-2 py-1 text-xs",
        isError
          ? "border-destructive/40 bg-destructive/5"
          : "border-rule bg-canvas",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-mono text-ink-muted">#{row.row_number}</span>
        <span className="text-ink font-display">
          {row.player?.display_name || "(missing name)"}
        </span>
        {row.player?.jersey_number ? (
          <span className="text-ink-muted">#{row.player.jersey_number}</span>
        ) : null}
        {row.player?.position ? (
          <span className="text-ink-muted">· {row.player.position}</span>
        ) : null}
        {row.guardian_email ? (
          <span className="text-ink-muted">· {row.guardian_email}</span>
        ) : null}
      </div>
      {!isError ? (
        <div className="text-ink-muted mt-0.5">
          {row.player_action} · {row.guardian_action}
        </div>
      ) : null}
      {isError && row.errors && row.errors.length > 0 ? (
        <div className="text-destructive mt-0.5">{row.errors.join("; ")}</div>
      ) : null}
    </li>
  );
}
